import React, { useMemo } from 'react';
import { ActivityIndicator, Image, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { useReportCardDocument, useReportCardPdf, useReportCardPhoto } from '../../hooks/useReportCards';
import {
  formatBandRange,
  formatFactorBreakdown,
  formatMarksOutOf,
  initialsOf,
  gradeBadgeColors,
  reportCardFileName,
} from '../../utils/reportCards';
import type { ReportCardDocument } from '../../types/reportCard';

interface Props {
  reportCardId: string;
  /** Native screens scroll here; the web page shell already scrolls. */
  scrollable: boolean;
  notify: (message: string, type: 'success' | 'error') => void;
}

/**
 * The in-app view of one published report card: a standard, mobile-friendly
 * layout of the server's document model. The options decide which optional
 * blocks (factors, comments, rank, remarks, grading scale) appear, as on the PDF.
 */
export function ReportCardDetailView({ reportCardId, scrollable, notify }: Props) {
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { doc, loading, error, reload } = useReportCardDocument(reportCardId);
  const { download, open, busyId } = useReportCardPdf(notify);

  if (loading) {
    return (
      <View style={styles.centered}>
        <LoadingSpinner message="Loading report card..." />
      </View>
    );
  }

  if (error || !doc) {
    return (
      <View style={styles.centered}>
        <FeatherIcon name="alert-circle" size={40} color={colors.textMuted} />
        <Text style={styles.stateText}>{error ?? 'Report card not found.'}</Text>
        <TouchableOpacity onPress={reload} accessibilityRole="button" style={styles.retryButton}>
          <Text style={styles.retryText}>Try again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const body = <ReportCardBody doc={doc} styles={styles} />;
  const fileName = reportCardFileName(doc.configName, doc.student.studentName);
  const saving = busyId === `save:${reportCardId}`;
  const opening = busyId === `open:${reportCardId}`;

  const actions = (
    <View style={styles.actions}>
      <TouchableOpacity
        style={[styles.primaryButton, saving && { opacity: 0.7 }]}
        onPress={() => download(reportCardId, fileName)}
        disabled={!!busyId}
        accessibilityRole="button">
        {saving ? (
          <ActivityIndicator color="#FFFFFF" size="small" />
        ) : (
          <>
            <FeatherIcon name="download" size={18} color="#FFFFFF" style={{ marginRight: 10 }} />
            <Text style={styles.primaryButtonText}>Download PDF</Text>
          </>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.secondaryButton, opening && { opacity: 0.7 }]}
        onPress={() => open(reportCardId, fileName)}
        disabled={!!busyId}
        accessibilityRole="button">
        {opening ? (
          <ActivityIndicator color={colors.primary} size="small" />
        ) : (
          <>
            <FeatherIcon name="external-link" size={18} color={colors.primary} style={{ marginRight: 10 }} />
            <Text style={styles.secondaryButtonText}>{Platform.OS === 'web' ? 'Open PDF in new tab' : 'Open PDF'}</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  if (!scrollable) {
    return (
      <View>
        {body}
        {actions}
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scrollContent}>
      {body}
      {actions}
    </ScrollView>
  );
}

type Styles = ReturnType<typeof createStyles>;

function InfoRow({ label, value, styles }: { label: string; value?: string | null; styles: Styles }) {
  if (!value) return null;
  return (
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );
}

function ReportCardBody({ doc, styles }: { doc: ReportCardDocument; styles: Styles }) {
  const { options, student } = doc;
  const photo = useReportCardPhoto(student.studentId, student.photoVersion);
  const showRank = options.showClassRank && student.rank != null;
  const overallGrade = gradeBadgeColors(student.overallGrade);

  // Marks only - no percentages or weightage anywhere on the card.
  const tiles: { label: string; value: string; accent?: { bg: string; text: string } }[] = [
    { label: 'Total marks', value: formatMarksOutOf(student.totalMarks, student.totalMaxMarks) },
    { label: 'Grade', value: student.overallGrade || '—', accent: student.overallGrade ? overallGrade : undefined },
  ];
  if (showRank) tiles.push({ label: 'Rank', value: doc.classSize > 0 ? `${student.rank} / ${doc.classSize}` : String(student.rank) });

  return (
    <View>
      {/* Header */}
      <View style={styles.headerCard}>
        <View style={styles.headerIcon}>
          <FeatherIcon name="award" size={22} color="#FFFFFF" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>{doc.configName}</Text>
        </View>
      </View>

      {/* Student */}
      <View style={styles.card}>
        <View style={styles.studentTop}>
          {photo ? (
            <Image source={{ uri: photo }} style={styles.avatar} accessibilityLabel={`Photo of ${student.studentName}`} />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <Text style={styles.avatarInitials}>{initialsOf(student.studentName)}</Text>
            </View>
          )}
          <View style={{ flex: 1 }}>
            <Text style={styles.studentName}>{student.studentName}</Text>
            {!!doc.className && <Text style={styles.studentClass}>{doc.className}</Text>}
          </View>
        </View>
        <InfoRow label="Student ID" value={student.studentIdentificationNumber} styles={styles} />      </View>

      {/* Summary tiles */}
      <View style={styles.tiles}>
        {tiles.map(tile => (
          <View key={tile.label} style={styles.tile}>
            <Text style={styles.tileLabel}>{tile.label}</Text>
            {tile.accent ? (
              <View style={[styles.gradeBadgeLarge, { backgroundColor: tile.accent.bg }]}>
                <Text style={[styles.tileValue, { color: tile.accent.text }]}>{tile.value}</Text>
              </View>
            ) : (
              <Text style={styles.tileValue}>{tile.value}</Text>
            )}
          </View>
        ))}
      </View>

      {/* Subjects */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Subjects</Text>
        <View style={styles.subjectHeader}>
          <Text style={[styles.subjectHeaderText, { flex: 1 }]}>SUBJECT</Text>
          <Text style={[styles.subjectHeaderText, styles.colMarks]}>MARKS</Text>
          <Text style={[styles.subjectHeaderText, styles.colGrade]}>GRADE</Text>
        </View>
        {student.subjects.length === 0 ? (
          <Text style={styles.emptyText}>No subjects on this report card.</Text>
        ) : (
          student.subjects.map((subject, idx) => {
            // No marks yet means no grade either, whatever the server sent.
            const hasMarks = subject.marks !== null && subject.marks !== undefined;
            const grade = hasMarks ? subject.grade : null;
            const badge = gradeBadgeColors(grade);
            const breakdown = options.showFactorColumns ? formatFactorBreakdown(subject.factors) : '';
            return (
              <View
                key={subject.subjectId}
                style={[styles.subjectRow, idx === student.subjects.length - 1 && { borderBottomWidth: 0 }]}>
                <View style={styles.subjectMain}>
                  <View style={{ flex: 1, paddingRight: 8 }}>
                    <Text style={styles.subjectName}>{subject.subjectName}</Text>
                    {!!breakdown && <Text style={styles.subjectFactors}>{breakdown}</Text>}
                    {subject.hasMissingFactor && (
                      <Text style={styles.subjectWarning}>Some marks are not yet recorded.</Text>
                    )}
                  </View>
                  <Text style={[styles.subjectMarks, styles.colMarks]}>{formatMarksOutOf(subject.marks, subject.maxMarks)}</Text>
                  <View style={styles.colGrade}>
                    {grade ? (
                      <View style={[styles.gradeBadge, { backgroundColor: badge.bg }]}>
                        <Text style={[styles.gradeBadgeText, { color: badge.text }]}>{grade}</Text>
                      </View>
                    ) : (
                      <Text style={styles.subjectMarks}>—</Text>
                    )}
                  </View>
                </View>
                {options.showSubjectComments && !!subject.comment && (
                  <Text style={styles.subjectComment}>{subject.comment}</Text>
                )}
              </View>
            );
          })
        )}
      </View>

      {/* Homeroom teacher's comment - always shown, as on the school's report card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Homeroom teacher's comment</Text>
        {!!doc.homeroomTeacherName && <Text style={styles.commentBy}>{doc.homeroomTeacherName}</Text>}
        {student.teacherRemarks?.trim() ? (
          <Text style={styles.remarks}>{student.teacherRemarks.trim()}</Text>
        ) : (
          <Text style={styles.emptyText}>No comment yet.</Text>
        )}
      </View>

      {/* Grading scale: Grade | Marks | Meaning */}
      {options.showGradingScale && doc.gradeBands.length > 0 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Grading scale{doc.gradeScaleName ? ` · ${doc.gradeScaleName}` : ''}</Text>
          <View style={styles.bandHeader}>
            <Text style={[styles.subjectHeaderText, styles.bandColGrade]}>GRADE</Text>
            <Text style={[styles.subjectHeaderText, styles.bandColMarks]}>MARKS</Text>
            <Text style={[styles.subjectHeaderText, { flex: 1 }]}>MEANING</Text>
          </View>
          {[...doc.gradeBands]
            .sort((a, b) => b.minPercent - a.minPercent)
            .map((band, idx, all) => {
              const badge = gradeBadgeColors(band.grade);
              return (
                <View
                  key={`${band.grade}-${band.minPercent}`}
                  style={[styles.bandRow, idx === all.length - 1 && { borderBottomWidth: 0 }]}>
                  <View style={styles.bandColGrade}>
                    <View style={[styles.gradeBadge, styles.bandBadge, { backgroundColor: badge.bg }]}>
                      <Text style={[styles.gradeBadgeText, { color: badge.text }]}>{band.grade}</Text>
                    </View>
                  </View>
                  <Text style={[styles.bandRange, styles.bandColMarks]}>
                    {formatBandRange(band.minPercent, band.maxPercent)}
                  </Text>
                  <Text style={styles.bandDescription}>{band.description?.trim() || '—'}</Text>
                </View>
              );
            })}
        </View>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) =>
  StyleSheet.create({
    scrollContent: { paddingHorizontal: 20, paddingTop: 4, paddingBottom: 32 },
    centered: { flex: 1, minHeight: 240, justifyContent: 'center', alignItems: 'center', padding: 24 },
    stateText: { marginTop: 12, fontSize: 14, color: colors.textSecondary, textAlign: 'center', lineHeight: 20 },
    retryButton: { marginTop: 8, paddingVertical: 4, paddingHorizontal: 8 },
    retryText: { fontSize: 14, fontWeight: '700', color: colors.primary },

    headerCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: isDark ? colors.primary : '#347FD5',
      borderRadius: 16,
      padding: 16,
      marginBottom: 16,
    },
    headerIcon: {
      width: 44,
      height: 44,
      borderRadius: 12,
      backgroundColor: 'rgba(255,255,255,0.18)',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    headerTitle: { color: '#FFFFFF', fontSize: 18, fontWeight: '800' },

    card: {
      backgroundColor: colors.card,
      borderRadius: 16,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: 16,
    },
    cardTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 10 },
    infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
    infoLabel: { fontSize: 13, color: colors.textSecondary, marginRight: 12 },
    infoValue: { flex: 1, fontSize: 13, fontWeight: '700', color: colors.textPrimary, textAlign: 'right' },

    tiles: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -4, marginBottom: 12 },
    tile: {
      flexGrow: 1,
      flexBasis: '45%',
      backgroundColor: colors.card,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 14,
      paddingHorizontal: 12,
      margin: 4,
      alignItems: 'center',
    },
    tileLabel: { fontSize: 11, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', marginBottom: 6 },
    tileValue: { fontSize: 20, fontWeight: '800', color: colors.textPrimary },
    gradeBadgeLarge: { paddingHorizontal: 12, paddingVertical: 2, borderRadius: 8 },

    subjectHeader: {
      flexDirection: 'row',
      paddingBottom: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    subjectHeaderText: { fontSize: 11, fontWeight: '700', color: colors.textSecondary },
    colMarks: { width: 92, textAlign: 'right' },
    colGrade: { width: 64, alignItems: 'flex-end', textAlign: 'right' },
    subjectRow: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.divider },
    subjectMain: { flexDirection: 'row', alignItems: 'center' },
    subjectName: { fontSize: 14, fontWeight: '600', color: colors.textPrimary },
    subjectFactors: { fontSize: 12, color: colors.textSecondary, marginTop: 3 },
    subjectWarning: { fontSize: 11, color: colors.warning, marginTop: 3 },
    subjectMarks: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
    subjectComment: { fontSize: 12, color: colors.textSecondary, fontStyle: 'italic', marginTop: 6, lineHeight: 18 },
    gradeBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
    gradeBadgeText: { fontSize: 12, fontWeight: '700' },
    emptyText: { fontSize: 13, color: colors.textSecondary, paddingVertical: 12 },

    remarks: { fontSize: 14, color: colors.textPrimary, lineHeight: 21 },
    commentBy: { fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginTop: -6, marginBottom: 8 },

    studentTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
    avatar: { width: 64, height: 64, borderRadius: 32, marginRight: 14, backgroundColor: colors.divider },
    avatarPlaceholder: { justifyContent: 'center', alignItems: 'center', backgroundColor: colors.primaryLight },
    avatarInitials: { fontSize: 22, fontWeight: '800', color: colors.primary },
    studentName: { fontSize: 17, fontWeight: '800', color: colors.textPrimary },
    studentClass: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },

    bandHeader: {
      flexDirection: 'row',
      paddingBottom: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    bandRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
    },
    bandColGrade: { width: 64, alignItems: 'flex-start' },
    bandColMarks: { width: 80 },
    bandBadge: { minWidth: 40, alignItems: 'center' },
    bandRange: { fontSize: 13, fontWeight: '600', color: colors.textPrimary },
    bandDescription: { flex: 1, fontSize: 13, color: colors.textSecondary },

    actions: { marginTop: 4, marginBottom: 16 },
    primaryButton: {
      backgroundColor: isDark ? colors.primary : '#17253C',
      borderRadius: 8,
      paddingVertical: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    primaryButtonText: { color: '#FFFFFF', fontSize: 15, fontWeight: '600' },
    secondaryButton: {
      marginTop: 10,
      borderRadius: 8,
      paddingVertical: 12,
      borderWidth: 1,
      borderColor: colors.primary,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    secondaryButtonText: { color: colors.primary, fontSize: 15, fontWeight: '600' },
  });
